/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'

import { P2, P4 } from 'components/Typography'
import Table, { TableRow } from 'components/Table/Table'
import { useSearchAndFilterTable } from 'contexts/SearchAndFilterTableContext'
import HeaderTable from 'components/Table/HeaderTable'
import { ShoppingCart } from 'feather-icons-react/build/IconComponents'
import CounterInput from 'components/CounterInput/CounterInput'
import { useCarrito } from 'contexts/CarritoContext'
import Link from 'components/Button/LinkUnderline'
import { useLocation, useNavigate } from 'react-router-dom'
import CurrencyFormat from 'react-currency-format'
import { useArticulos } from 'contexts/ArticulosContext'
import StatusTag from 'components/Tag/StatusTag'
import { Tooltip } from 'react-tooltip'
import { showToast } from 'components/Notification/ToastNotification'
import { compressLongText, isLongText, replaceText } from 'utils/textProcess'
import { useWindowWidth } from 'contexts/WindowWidthContext'
import { Button } from 'components/index'

const LinkTable = styled(Link)`
  line-break: anywhere;
`

const ShoppingCartIcon = styled(ShoppingCart)`
  margin: 30px 20px;
`

const CustomHeader = styled(HeaderTable)`
  background-color: ${({ theme }) => theme.colors.neutral.white};
  @media (min-width: 440px) and(max-width: 1366px) {
    grid-template-columns: 8% 18% 8% 10% 6% 6% 8% 10% 8%;
  }
`

const CustomTable = styled(Table)`
  ${TableRow} {
    @media (min-width: 440px) and(max-width: 1366px) {
      grid-template-columns: 8% 18% 8% 10% 6% 6% 8% 10% 8%;
    }
  }
`

const CategoryContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const SubTitleTable = styled(P2)`
  margin: 15px;
`

const EmptyTableData = styled.div`
  padding: 23px 30px;
  background-color: ${({ theme }) => theme.colors.neutral.white};
  text-align: center;
`

const ScrolleableTable = styled.div`
  height: 50vh;
  overflow: auto;
`

const TableCell = styled.div`
  display: flex;
  align-items: center;
  cursor: ${({ $precioReal }) => ($precioReal ? 'pointer' : 'initial')};
`

const VerticalTableCell = styled(TableCell)`
  flex-direction: column;
  align-items: start;
  gap: 6px;
`

const CustomStatusTag = styled(StatusTag)`
  font-size: 12px;
`

const PromoTag = styled(CustomStatusTag)`
  background-color: ${({ theme }) => theme.colors.primary.darkBlue};
`

const FooterSection = styled.div`
  display: flex;
`

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
`

const IdCol = styled.div`
  display: block;
`

const isEstadoSuspendido = (estado) => {
  switch (estado) {
    case '1':
      return false // Activo
    case 2:
    case 3:
    case 10:
    case 31:
    default:
      return true // Suspendido
  }
}

const devolverPrecioReal = (articulo) => {
  const precioReal = parseFloat(articulo.precio_real).toLocaleString('es-ar', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 3,
  })

  return precioReal
}

function actualizarCantidadCarrito(data, id, id_articulo, cantidad) {
  const categoria = data.find((cat) => cat.id === id)

  if (!categoria) {
    return data
  }

  const articulo = categoria.articulos.find(
    (art) => art.id_articulo === id_articulo,
  )

  if (!articulo) {
    return data
  }

  articulo.cantidad_carrito = cantidad

  return data
}

const formatData = (
  { articulos, puedePedir, categoriaId },
  onClickActualizarCarrito,
  navigateToProductoPage,
  width,
) =>
  articulos.map((e) => ({
    ...(width > 440 && {
      Categoria: (
        <TableCell>
          <CustomStatusTag
            estado={isEstadoSuspendido(e.estado) ? 'Suspendido' : 'Activo'}
          />
        </TableCell>
      ),
    }),
    Producto: (
      <>
        <VerticalTableCell
          data-tooltip-id={
            isLongText(e.descripcion, 24) ? 'productoDescripcion' : undefined
          }
          data-tooltip-content={
            isLongText(e.descripcion, 24) ? e.descripcion : undefined
          }
          data-tooltip-place="top"
        >
          {e.id_promocion !== '0' && <PromoTag estado={'PROMO'} />}
          {/* prettier-ignore */}
          <div id={`input_${e.id_articulo}`} tabIndex={-1}>
            <LinkTable
              onClick={() => {
                navigateToProductoPage(e.id_articulo)
              }}
            >
              {compressLongText(e.descripcion, 24)}
            </LinkTable>
          </div>
        </VerticalTableCell>
        {isLongText(e.descripcion, 24) && (
          <Tooltip id="productoDescripcion" delayShow={50} />
        )}
      </>
    ),
    'UNI/MEDIDA': (
      <TableCell>
        <P4>{e.gramaje + ' ' + e.unidad_medida}</P4>
      </TableCell>
    ),
    ...(width > 440 && {
      EAN: (
        <TableCell>
          {e.id_col && e.id_col !== '0' ? (
            <>
              <IdCol>
                <P4>{e.ean13}</P4>
                <P4>({e.id_col})</P4>
              </IdCol>
            </>
          ) : (
            <P4>{e.ean13}</P4>
          )}
        </TableCell>
      ),
      'UNI/BULTO': (
        <TableCell>
          <P4>{e.bultos_camada}</P4>
        </TableCell>
      ),
      'BULTO/PALLETS': (
        <TableCell>
          <P4>{e.bultos_piso * e.pisos_pallet}</P4>
        </TableCell>
      ),
      'BULTO/PISO': (
        <TableCell>
          <P4>{e.bultos_piso}</P4>
        </TableCell>
      ),
      'BULTO/PALLET': (
        <TableCell>
          <P4>{e.pisos_pallet}</P4>
        </TableCell>
      ),
    }),
    PRECIO: (
      <>
        <TableCell
          data-tooltip-id="Precio"
          data-tooltip-content={e.precio_real ? devolverPrecioReal(e) : ''}
          data-tooltip-place="top"
          $precioReal={e.precio_real}
        >
          <P4>
            <CurrencyFormat
              value={parseFloat(e.precio)}
              displayType={'text'}
              thousandSeparator={'.'}
              decimalSeparator={','}
              prefix={' $'}
              decimalScale={3}
              fixedDecimalScale={true}
            />
          </P4>
        </TableCell>
        <Tooltip id="Precio" delayShow={50} />
      </>
    ),
    ...(width > 440 && {
      OBSERVACIÓN: (
        <TableCell>
          <P4
            title={`${e.multiplo !== '0' && e.multiplo !== '1' ? `Múltiplo: ${e.multiplo}` : ''}${
              e.multiplo !== '0' &&
              e.multiplo !== '1' &&
              e.observacion &&
              e.observacion.length > 0
                ? ' - '
                : ''
            }${e.observacion && e.observacion.length > 0 ? ` ${e.observacion}` : ''}`}
          >
            {e.multiplo !== '0' && e.multiplo !== '1'
              ? `Múltiplo: ${e.multiplo}`
              : ''}
            {e.multiplo !== '0' &&
            e.multiplo !== '1' &&
            e.observacion &&
            e.observacion.length > 0
              ? ' - '
              : ''}
            {e.observacion && e.observacion.length > 0
              ? ` ${compressLongText(e.observacion, 34)}`
              : ''}
          </P4>
        </TableCell>
      ),
    }),
    CANTIDAD: (
      <TableCell>
        <CounterInput
          amount={e.cantidad_carrito}
          onClick={async (cant) => {
            await onClickActualizarCarrito(categoriaId, e.id_articulo, cant)
            showToast(`Artículo modificado en el carrito!`, 'validacion')
          }}
          disabled={!puedePedir || isEstadoSuspendido(e.estado)}
        />
      </TableCell>
    ),
  }))

const ProductosTable = ({ puedePedir, nombreProveedor, idProveedor }) => {
  const location = useLocation()
  const focusInput = location.state?.focusInput
  const { agregarItem } = useCarrito()
  const { setArticulos, articulos, getArticulos } = useArticulos()
  const { width } = useWindowWidth()
  const { data } = useSearchAndFilterTable()
  const navigate = useNavigate()
  const tableRef = useRef(null)

  const [articlesPerCategory, setArticlesPerCategory] = useState({})
  const [totalArticlesLoaded, setTotalArticlesLoaded] = useState(0)

  const LOAD_STEP = 20

  // Clave por proveedor para persistir, entre la vista de surtido y el detalle
  // de artículo, la cantidad de artículos cargados y la posición del scroll.
  const storageKey = idProveedor ? `surtidoScroll_${idProveedor}` : null

  // scrollTop actual del contenedor de la tabla (se actualiza en cada scroll).
  const scrollTopRef = useRef(0)
  // Espejos de estado para leerlos al desmontar sin closures obsoletos.
  const articlesPerCategoryRef = useRef(articlesPerCategory)
  const totalArticlesLoadedRef = useRef(totalArticlesLoaded)
  // scrollTop pendiente de aplicar una vez repuestas las filas.
  const pendingScrollTopRef = useRef(null)
  // scroll de la ventana pendiente de aplicar (por si el scroll relevante es el
  // de la página y no el del contenedor interno de la tabla).
  const pendingWindowScrollRef = useRef(null)
  // Indica que la posición ya fue restaurada desde el snapshot, para no pisarla
  // con el scrollIntoView de focusInput.
  const scrollRestoredRef = useRef(false)

  // Snapshot leído una sola vez al primer render (antes de los efectos).
  const [restoreData] = useState(() => {
    try {
      const key = idProveedor ? `surtidoScroll_${idProveedor}` : null
      const raw = key ? sessionStorage.getItem(key) : null
      const snap = raw ? JSON.parse(raw) : null
      return snap && snap.totalArticlesLoaded > 0 ? snap : null
    } catch (e) {
      return null
    }
  })
  // Mientras esté pendiente, re-aplicamos la restauración en cada cambio de
  // `data`. Esto es clave porque los filtros por defecto (activos/todos) se
  // aplican después del montaje y cambian `data`; queremos que la última
  // restauración (ya con la data filtrada) sea la que prevalezca. Se cancela en
  // cuanto el usuario interactúa (scroll manual, click, tecla, "Cargar más").
  const restorePendingRef = useRef(!!restoreData)

  useEffect(() => {
    articlesPerCategoryRef.current = articlesPerCategory
  }, [articlesPerCategory])

  useEffect(() => {
    totalArticlesLoadedRef.current = totalArticlesLoaded
  }, [totalArticlesLoaded])

  // Guarda el snapshot en el momento de entrar al detalle de un artículo. Es la
  // única vía por la que se abandona el surtido hacia el detalle, así que
  // capturar acá el scroll + la cantidad cargada evita que montajes/desmontajes
  // transitorios (re-fetch, auto-selección de zona) pisen el valor bueno.
  const persistSnapshot = () => {
    if (!storageKey) return
    try {
      const snap = {
        articlesPerCategory: articlesPerCategoryRef.current,
        totalArticlesLoaded: totalArticlesLoadedRef.current,
        scrollTop: scrollTopRef.current,
        windowScrollY: window.scrollY || window.pageYOffset || 0,
      }
      sessionStorage.setItem(storageKey, JSON.stringify(snap))
    } catch (e) {
      /* noop */
    }
  }

  // Aplica el scroll pendiente (contenedor interno + ventana) reintentando con
  // requestAnimationFrame hasta que el contenido restaurado tenga altura
  // suficiente (imágenes, layout, etc.).
  const applyPendingScroll = () => {
    if (
      pendingScrollTopRef.current == null &&
      pendingWindowScrollRef.current == null
    )
      return

    const targetTable = pendingScrollTopRef.current
    const targetWindow = pendingWindowScrollRef.current
    let attempts = 0

    const tryScroll = () => {
      const el = tableRef.current
      if (!el) return

      if (targetTable != null) el.scrollTop = targetTable
      if (targetWindow != null) window.scrollTo(0, targetWindow)

      attempts += 1
      const tableOk = targetTable == null || el.scrollTop >= targetTable - 2
      const windowOk =
        targetWindow == null ||
        (window.scrollY || window.pageYOffset || 0) >= targetWindow - 2

      if ((!tableOk || !windowOk) && attempts < 30) {
        requestAnimationFrame(tryScroll)
      } else {
        pendingScrollTopRef.current = null
        pendingWindowScrollRef.current = null
      }
    }
    requestAnimationFrame(tryScroll)
  }

  // Persiste también al desmontar (cubre salir con el botón "atrás" del
  // navegador u otras vías). NO tocamos el snapshot mientras la restauración
  // sigue pendiente: durante los montajes/desmontajes transitorios (recargas de
  // artículos, auto-zona) guardar un scrollTop recortado degradaría el valor
  // bueno hasta 0. Solo persistimos una vez que el usuario tomó el control.
  useEffect(() => {
    return () => {
      if (restorePendingRef.current) return
      const winScroll = window.scrollY || window.pageYOffset || 0
      if (scrollTopRef.current > 0 || winScroll > 0) {
        persistSnapshot()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Timer que libera la restauración. Se reprograma (debounce) en cada actividad
  // de datos, de modo que `restorePending` sigue activo durante toda la ráfaga
  // de recargas/re-montajes que ocurre al volver (por más que dure varios
  // segundos) y recién se libera cuando la data se estabiliza. Como el scroll
  // manual del usuario NO cambia `data`, no hay riesgo de pisarlo; y una vez
  // liberado, los cambios posteriores (filtro/búsqueda/carrito) usan el
  // comportamiento normal (resetear a la primera página).
  const clearTimerRef = useRef(null)
  const scheduleRestoreRelease = () => {
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current)
    clearTimerRef.current = setTimeout(() => {
      restorePendingRef.current = false
    }, 2000)
  }

  useEffect(() => {
    return () => {
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current)
    }
  }, [])

  useEffect(() => {
    const hasArticles =
      Array.isArray(data) && data.some((c) => c?.articulos?.length > 0)

    // Al volver del detalle restauramos la cantidad de artículos que estaban
    // cargados (incluidos los sumados con "Cargar más") para poder reponer luego
    // la posición del scroll. Llenamos de forma greedy hasta el total guardado
    // para reproducir el mismo layout aun después de que se apliquen los filtros
    // por defecto. Mientras `restorePendingRef` siga activo, esto se re-aplica
    // en cada cambio de `data`.
    if (restorePendingRef.current && hasArticles) {
      const target = restoreData.totalArticlesLoaded
      const restored = {}
      let total = 0
      for (let category of data) {
        if (total >= target) break
        const toLoad = Math.min(category.articulos.length, target - total)
        if (toLoad > 0) {
          restored[category.id] = toLoad
          total += toLoad
        }
      }
      if (total > 0) {
        pendingScrollTopRef.current = restoreData.scrollTop || 0
        pendingWindowScrollRef.current = restoreData.windowScrollY || 0
        scrollRestoredRef.current = true
        scheduleRestoreRelease()
        setArticlesPerCategory(restored)
        setTotalArticlesLoaded(total)
        return
      }
    }

    let initialArticles = {}
    let total = 0

    for (let category of data) {
      const toLoad = Math.min(category.articulos.length, LOAD_STEP - total)
      initialArticles[category.id] = toLoad
      total += toLoad

      if (total >= LOAD_STEP) break
    }

    setArticlesPerCategory(initialArticles)
    setTotalArticlesLoaded(total)
  }, [data])

  // Repone la posición del scroll una vez que las filas restauradas ya se
  // renderizaron (useLayoutEffect corre antes del pintado; el reintento con rAF
  // cubre el caso en que la altura del contenido todavía no está lista).
  useLayoutEffect(() => {
    applyPendingScroll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articlesPerCategory])

  const loadMoreArticles = () => {
    restorePendingRef.current = false
    let totalLoaded = totalArticlesLoaded
    const updatedArticlesPerCategory = { ...articlesPerCategory }

    for (let category of data) {
      const articlesCount = updatedArticlesPerCategory[category.id] || 0

      if (
        articlesCount < category.articulos.length &&
        totalLoaded < totalArticlesLoaded + LOAD_STEP
      ) {
        const remainingArticles = category.articulos.length - articlesCount
        const toLoad = Math.min(
          remainingArticles,
          LOAD_STEP - (totalLoaded - totalArticlesLoaded),
        )

        updatedArticlesPerCategory[category.id] = articlesCount + toLoad
        totalLoaded += toLoad
      }

      if (totalLoaded >= totalArticlesLoaded + LOAD_STEP) break
    }

    setArticlesPerCategory(updatedArticlesPerCategory)
    setTotalArticlesLoaded(totalLoaded)
  }
  // prettier-ignore
  const navigateToProductoPage = useCallback(async (idArticulo) => {
    persistSnapshot()
    navigate(`/comprar/${replaceText(nombreProveedor, ' ', '-')}/${idArticulo}`,{ state: { focusInput: idArticulo } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAgregarArticulos = useCallback(
    async (id_categoria, id_articulo, cantidad) => {
      try {
        await agregarItem(id_articulo, cantidad, idProveedor)
      } catch (error) {
        console.error('Error al actualizar el carrito:', error)
        await getArticulos(idProveedor)
      }
      setArticulos({
        ...articulos,
        articulos: actualizarCantidadCarrito(
          articulos.articulos,
          id_categoria,
          id_articulo,
          cantidad,
        ),
      })
    },
    [],
  )

  const headers = [
    ...(width > 440 ? [{ name: 'Estado', isOrderable: false }] : []),
    { name: 'Producto', isOrderable: false },
    { name: 'Uni / Medida', isOrderable: false },
    ...(width > 440
      ? [
          { name: 'EAN', isOrderable: false },
          { name: 'Uni / Bulto', isOrderable: false },
          { name: 'Bulto / Pallets', isOrderable: false },
          { name: 'Bulto / Piso', isOrderable: false },
          { name: 'Piso / Pallets', isOrderable: false },
        ]
      : []),
    { name: 'Precio Neto', isOrderable: false },
    ...(width > 440 ? [{ name: 'Observación', isOrderable: false }] : []),
    { name: 'Cantidad', isOrderable: false },
  ]
  // prettier-ignore
  useEffect(() => {
    // Si ya restauramos la posición exacta desde el snapshot, no la pisamos.
    if (scrollRestoredRef.current) return
    if (focusInput) {
      document.getElementById(`input_${focusInput}`)?.scrollIntoView({behavior: 'smooth'})
      document.getElementById(`input_${focusInput}`)?.focus()
    }
  }, [focusInput, articlesPerCategory])

  return (
    <>
      <CustomHeader
        numerate={false}
        headers={headers}
        gridTemplate={
          //width > 440 ? '8% 18% 6% 12% 6% 6% 10% 10% 10%' : '30% 16% 20% 10%'
          width > 440 ? '8% 18% 6% 12% 4% 4% 5% 5% 8% 8% 8%' : '30% 16% 20% 10%'
        }
      />
      {data?.length > 0 ? (
        <ScrolleableTable
          ref={tableRef}
          onScroll={(e) => {
            scrollTopRef.current = e.currentTarget.scrollTop
          }}
        >
          {data.reduce(
            (sum, categoria) => sum + categoria.articulos.length,
            0,
          ) > 80 ? (
            <>
              {data
                .filter((categoria) => categoria.articulos.length > 0)
                .filter((categoria) =>
                  Object.keys(articlesPerCategory).includes(categoria.id),
                )
                .map((categoria, index) => {
                  return (
                    <CategoryContainer key={index}>
                      <SubTitleTable>{categoria.descripcion}</SubTitleTable>

                      <CustomTable
                        data={formatData(
                          {
                            categoria: categoria.descripcion,
                            articulos: categoria.articulos
                              .sort(
                                (a, b) =>
                                  isEstadoSuspendido(a.estado) -
                                  isEstadoSuspendido(b.estado),
                              )
                              .slice(0, articlesPerCategory[categoria.id] || 0),
                            puedePedir,
                            categoriaId: categoria.id,
                          },
                          handleAgregarArticulos,
                          navigateToProductoPage,
                          width,
                        )}
                        numerate={false}
                        gridTemplate={
                          width > 440
                            ? '8% 18% 6% 12% 4% 4% 5% 5% 8% 8% 8%'
                            : '32% 12% 18% 10%'
                        }
                      />
                    </CategoryContainer>
                  )
                })}

              {totalArticlesLoaded <
                data.reduce((acc, cat) => acc + cat.articulos.length, 0) && (
                <ButtonContainer>
                  <Button
                    onClick={loadMoreArticles}
                    text="Cargar más"
                    size="small"
                  />
                </ButtonContainer>
              )}
            </>
          ) : (
            data
              .filter((categoria) => categoria.articulos.length > 0)
              .map((categoria, index) => (
                <div key={index}>
                  <SubTitleTable>{categoria.descripcion}</SubTitleTable>
                  <CustomTable
                    data={formatData(
                      {
                        categoria: categoria.descripcion,
                        articulos: categoria.articulos.sort(
                          (a, b) =>
                            isEstadoSuspendido(a.estado) -
                            isEstadoSuspendido(b.estado),
                        ),
                        puedePedir,
                        categoriaId: categoria.id,
                      },
                      handleAgregarArticulos,
                      navigateToProductoPage,
                      width,
                    )}
                    numerate={false}
                    gridTemplate={
                      width > 440
                        ? '8% 18% 6% 12% 4% 4% 5% 5% 8% 8% 8%'
                        : '32% 12% 18% 10%'
                    }
                  />
                </div>
              ))
          )}
        </ScrolleableTable>
      ) : (
        <EmptyTableData>No se encontraron articulos</EmptyTableData>
      )}
      <FooterSection>
        <Link href="/mi-carrito">
          <ShoppingCartIcon /> Ver mi carrito
        </Link>
      </FooterSection>
    </>
  )
}

export default ProductosTable
